import ProductInfo from '@/fsd/entities/product/ui/ProductInfo/ProductInfo';
import { prepareStatusClass } from '@/fsd/entities/purchase';
import { formatAsPrice } from '@/fsd/shared/lib/helpers/formatAsPrice/formatAsPrice';
import { addThousandsSeparators } from '@/fsd/shared/lib/helpers/formatNumberWithThousandsSeparators/addThousandsSeparators';
import StatusBlock from '@/fsd/shared/ui/status-block/status-block';
import TruncatedTextWithTooltip from '@/fsd/shared/ui/TruncatedTextWithTooltip/TruncatedTextWithTooltip';
import cx from 'classnames';
import { format, parseISO } from 'date-fns';
import { Column } from '../../model/types/Column';
import PlanColumn from './Plan';
import style from './TableItem.module.scss';

interface IProps {
  column?: Column;
  item: any;
}

export const prepareDateDifference = (prev, current) => {
  const prevDate = new Date(prev);
  const currentDate = new Date(current);

  const diffInMilliseconds = Math.abs(currentDate - prevDate);

  const days = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (diffInMilliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );

  return (
    <span>
      Время доставки: {days}д. {hours}ч.
    </span>
  );
};

export const prepareColumn = (item: any, column: IProps['column']) => {
  switch (column?.type) {
    case 'photo':
      return (
        <div className={style.photo}>
          <img src={item[column.id]} />
        </div>
      );

    case 'pay':
      return <div className={style.pay}>{formatAsPrice(item[column.id])}</div>;

    case 'count':
      return (
        <div className={style.count}>
          {addThousandsSeparators(item[column.id])}
        </div>
      );

    case 'object':
      return (
        <TruncatedTextWithTooltip text={item[column.id]?.name} maxLines={1} />
      );

    case 'id':
      return <div className={style.center}>{item[column.id]}</div>;

    case 'status':
      return (
        <StatusBlock
          label={item[column.id]}
          status={prepareStatusClass(item[column.id])}
        />
      );

    case 'date':
      return (
        <div className={style.date}>
          {format(parseISO(item[column.id]), 'dd.MM.yyyy')}
        </div>
      );

    case 'sending':
      return (
        <div className={style.moving}>
          <div className={style.warehouse}>
            <p title={item.sender_warehouse.name}>
              {item.sender_warehouse.name}
            </p>
          </div>
          <div className={style.bottom}>
            <span className={cx(style.description, style.onWay)}>
              Отправка:{' '}
            </span>
            <span className={style.date}>
              {format(parseISO(item[column.id]), 'dd.MM.yyyy')}
            </span>
            <span>{format(parseISO(item[column.id]), 'HH:mm')}</span>
          </div>
        </div>
      );

    case 'arrival':
      return (
        <div className={style.moving}>
          <div className={style.warehouse}>
            <p title={item.sender_warehouse.name}>
              {item.destination_warehouse.name}
            </p>
          </div>
          <div className={style.bottom}>
            <span className={cx(style.description, style.onWay)}>
              Прибытие:{' '}
            </span>
            <span className={style.date}>
              {format(parseISO(item[column.id]), 'dd.MM.yyyy')}
            </span>
            <span>{format(parseISO(item[column.id]), 'HH:mm')}</span>
          </div>
        </div>
      );

    case 'delivery_date':
      return (
        <div className={style.moving}>
          <div>{prepareDateDifference(item.sending, item.delivery_date)}</div>
          <div className={style.bottom}>
            <span
              className={cx(
                style.description,
                item.status === 'Заказ выполнен' ? style.success : style.warning
              )}
            >
              {item.status === 'Заказ выполнен' ? 'Принят' : 'Ожидается'}:{' '}
            </span>
            <span className={style.date}>
              {format(parseISO(item.delivery_date), 'dd.MM.yyyy')}
            </span>
            <span>{format(parseISO(item.delivery_date), 'HH:mm')}</span>
          </div>
        </div>
      );

    case 'plan':
      return <PlanColumn plan={item.plan.plan} fact={item.plan.fact} />;

    case 'name':
      return (
        <ProductInfo
          name={item.name}
          barcodes={item.barcodes}
          vendorCode={item.article}
        />
      );

    case 'warehouse': {
      const warehouseId = Number(column?.id.split('_')[1]);
      const warehouse = item.warehouses?.find((w) => w.id === warehouseId);
      return (
        <div className={style.count}>
          {addThousandsSeparators(warehouse?.quantity || 0)}
        </div>
      );
    }

    default:
      return (
        column && (
          <TruncatedTextWithTooltip text={item[column?.id]} maxLines={1} />
        )
      );
  }
};

const TableItem = ({ item, column }: IProps) => {
  return prepareColumn(item, column);
};

export default TableItem;
